import React from 'react';
import _ from 'lodash';
import moment from 'moment';

import { isForemanRoleId, isPartnerRoleId } from '../../utilities/roleUtility';
import RequireIcon from '../RequireIcon';
import { deleteControlCode, stringToNumber } from '../../utilities/stringUtil';
import {
  API_FLAG,
  USER_ROLES,
  CALL_FROM_PAGE,
  APPROVAL_STATUS_FIELDTREE,
} from '../../utilities/constants';
import { FormElement } from '../CommonFormComponents';
import DropdownSelector from '../dropdowns/DropdownSelector';
import FieldTreeService from '../../services/FieldTrees';
import { TrashIcon } from '../icons/TrashIcon';
import { messageUtility } from '../../utilities/messageUtility';
import {
  HEAD_OFFICE_OPTION,
  PartnerCompanyBranchDropdown,
} from '../PartnerCompanyBranchDropdown';
import SessionStorageService from '../../services/utilities/SessionStorageService';
import Tippy from '../ReactTippy';

const ACCESS_CONTROL_INFO_RECORDING_START_DATE = '2022/05/29'; // この値の意味についてはA-818のUSDMを確認してください。課題：BLDE_V2-34813

/**
 * 編集の時だけは表示させたくない「必須」アイコン
 * 「職種」以外に使う
 */
const SwitchableRequireIcon = ({ newMode = true }) => {
  return newMode && <RequireIcon />;
};

/**
 * @param fieldId
 * @param values
 * @param disabledFields
 * @param forms
 * @param messages
 * @param newMode
 * @returns {JSX.Element}
 * @constructor
 */
export const TreePartnerUserForm = ({
  fieldId,
  values,
  disabledFields,
  forms,
  messages,
  newMode,
  onSelect,
  isManagerPermission,
  isTargetRoleId,
  partnerCompanyBranchPreviewName,
  initPartnerCompanyBranchId,
}) => {
  const companyId = SessionStorageService.getCompanyId();
  const {
    inviter_user_fullname,
    inviter_company_name,
    created_at,
    blocked_on,
    blocked_at,
    field_accessible_at,
    approval_flag,
  } = values;
  const isNewVersionData = moment(created_at).isSameOrAfter(
    ACCESS_CONTROL_INFO_RECORDING_START_DATE,
    'day'
  );
  const isBlockedUser = !_.isEmpty(blocked_on);
  const japaneseEmptyString = 'ー';
  const oldVersionFieldAccessibleTimeString = `${ACCESS_CONTROL_INFO_RECORDING_START_DATE}以前`;

  /**
   * ロールを元にユーザー種別を選択するプルダウンを表示
   *
   * @returns
   * @memberof FieldTreePartnerUserForm
   */
  const roleCodeInput = () => {
    // 選んだユーザのロール
    const userRoleCode = values.role_code;
    // 取得できないタイミングがあるため
    if (
      userRoleCode === '0' ||
      userRoleCode === 0 ||
      _.isUndefined(userRoleCode)
    ) {
      return;
    }

    // 選んだユーザのロール(int)
    const roleId = parseInt(userRoleCode, 10);
    const { partner, foreman } = USER_ROLES;
    let targetRole;
    if (isPartnerRoleId(roleId)) {
      // 協力管理者の時
      targetRole = partner;
    } else if (isForemanRoleId(roleId)) {
      // 職長の時
      targetRole = foreman;
    } else {
      throw new Error('協力管理者、職長以外のありえないロールIDがあります.');
    }

    return <input type='text' disabled value={targetRole.name} />;
  };

  /**
   * 職種削除可能かどうか
   * @returns boolean
   * @param {*} fieldTreeUserJob
   */
  const isJobDeleteAble = fieldTreeUserJob => {
    return isAble(fieldTreeUserJob);
  };

  /**
   * 職種追加可能かどうか
   * @returns boolean
   * @param {*} fieldTreeUserJob
   */
  const isJobAddAble = fieldTreeUserJob => {
    return isAble(fieldTreeUserJob);
  };

  /**
   * 操作可能かどうかの判定
   * true: 可能、false：不可
   * @return boolean
   */
  const isAble = fieldTreeUserJob => {
    // console.log('FieldTreePartnerUserForm.jsx isAble', fieldTreeUserJob);

    // すべてのキーが存在しない場合は新規登録とみなし操作可能とする
    if (
      !_.has(fieldTreeUserJob, 'crane_uses_in_use_flag') &&
      !_.has(fieldTreeUserJob, 'gate_passages_in_use_flag') &&
      !_.has(fieldTreeUserJob, 'machinery_uses_in_use_flag') &&
      !_.has(fieldTreeUserJob, 'work_results_in_use_flag') &&
      !_.has(fieldTreeUserJob, 'work_schedules_in_use_flag')
    ) {
      return true;
    }

    const res =
      // クレーン(0: 使用なし、1: 使用あり)
      fieldTreeUserJob.crane_uses_in_use_flag +
      // ゲート(0: 使用なし、1: 使用あり)
      fieldTreeUserJob.gate_passages_in_use_flag +
      // 揚重機(0: 使用なし、1: 使用あり)
      fieldTreeUserJob.machinery_uses_in_use_flag +
      // 作業実績(0: 使用なし、1: 使用あり)
      fieldTreeUserJob.work_results_in_use_flag +
      // 作業予定(0: 使用なし、1: 使用あり)
      fieldTreeUserJob.work_schedules_in_use_flag;

    // 一つでも使用ありがあるとFalse
    return res === 0;
  };

  // 職種コンポーネント
  const renderJobsContainer = () => {
    if (!_.has(values, 'field_tree_user_jobs')) {
      return;
    }

    return _.map(values.field_tree_user_jobs, (value, index) => {
      return (
        <div
          className='fieldstree_jobs_item'
          key={`field_tree_partner_user_form_${value.id}`}
        >
          {isJobAddAble(value) ? (
            <React.Fragment>
              <DropdownSelector
                className='jobs_item'
                label='職種を選択'
                multiple={false}
                selectorName='job_id'
                api={params => FieldTreeService.privateJobsList(params)}
                apiParams={{
                  display_flag: API_FLAG.On,
                  field_id: fieldId,
                }}
                apiSearchKey='name'
                selectedValues={[String(value.job_id)]}
                selectedName={value.job_name}
                keyName='name'
                valueName='id'
                apiBindDataKey={'jobs'}
                onSelect={(...args) => {
                  const fieldTreeJobs = _.cloneDeep(
                    values.field_tree_user_jobs
                  );
                  if (args[1] === '0') {
                    fieldTreeJobs[index] = {
                      id: fieldTreeJobs[index].id,
                      job_id: 0,
                      job_name: '職種を選択',
                    };
                  } else {
                    fieldTreeJobs[index] = {
                      id: fieldTreeJobs[index].id,
                      job_id: stringToNumber(args[1]),
                    };
                  }

                  forms.set('field_tree_user_jobs', fieldTreeJobs);
                  setTimeout(() => forms.isValid(true, 'job_id'), 100);
                }}
                onClose={() => forms.isValid(true, 'job_id')}
                notUncheck={true}
                dropdownSelectStyle={{}}
              />
            </React.Fragment>
          ) : (
            <input type='text' disabled defaultValue={value.job_name} />
          )}
          {/* 削除ボタン */}
          {!isJobDeleteAble(value) || (newMode && index === 0) ? null : (
            <button
              className='jobs_item_delete btn-icon--xs sys-delete form-list__element--btn_delete'
              onClick={() => {
                removeJob(index);
              }}
            >
              <TrashIcon className='icon--2_4x form-list__trash-icon' />
            </button>
          )}
        </div>
      );
    });
  };

  //職種を追加
  const addJob = () => {
    const fieldTreeJobs = _.cloneDeep(values.field_tree_user_jobs);
    fieldTreeJobs.push({
      id: `New_${_.uniqueId()}`,
      job_id: 0,
      job_name: '職種を選択',
    });

    forms.set('field_tree_user_jobs', fieldTreeJobs);
  };

  /**
   * 職種プルダウンの右のXボタンを押したときの処理
   *
   * @param {*} index
   * @memberof FieldTreePartnerUserForm
   */
  const removeJob = index => {
    const fieldTreeJobs = _.cloneDeep(values.field_tree_user_jobs);
    fieldTreeJobs.splice(index, 1);
    forms.set('field_tree_user_jobs', fieldTreeJobs);

    setTimeout(() => forms.isValid(true, 'job_id'), 100);
  };

  const isTelEmptyAndDisabled = () => {
    return (
      _.isEmpty(values.tel1) &&
      _.isEmpty(values.tel2) &&
      _.isEmpty(values.tel3) &&
      disabledFields.includes('tel1') &&
      disabledFields.includes('tel2') &&
      disabledFields.includes('tel3')
    );
  };

  const isDisplayInviterUserTooltip = () => {
    if (!isNewVersionData) {
      return false;
    }
    return !!inviter_company_name && !!inviter_user_fullname;
  };

  const getDisplayInviterUser = () => {
    if (!isNewVersionData) {
      return japaneseEmptyString;
    }
    const inviterCompanyName = _.defaultTo(inviter_company_name, '');
    const inviterUserFullName = _.defaultTo(inviter_user_fullname, '');
    if (_.isEmpty(inviterCompanyName) || _.isEmpty(inviterUserFullName)) {
      return japaneseEmptyString;
    }
    const spaceJapanese = '　';
    return `${inviterCompanyName}${spaceJapanese}${inviterUserFullName}`;
  };

  const getDisplayFieldAccessibleTime = () => {
    if (approval_flag !== APPROVAL_STATUS_FIELDTREE.approval.id) {
      return japaneseEmptyString;
    }
    if (field_accessible_at) {
      return field_accessible_at;
    }
    return isBlockedUser
      ? japaneseEmptyString
      : oldVersionFieldAccessibleTimeString;
  };

  const getCreatedTime = () => _.defaultTo(created_at, japaneseEmptyString);

  const getBlockedTime = () => _.defaultTo(blocked_at, japaneseEmptyString);

  return (
    <ul className='form-list form-list-default form-list-default--space'>
      {/* 職長の場合のみ職種入力欄の表示 */}
      {_.isNumber(values.role_code) && isForemanRoleId(values.role_code) && (
        <li className='form-list__item'>
          <div className='form-list__label'>
            <span className='label'>
              職種
              <RequireIcon />
            </span>
          </div>
          <FormElement
            className='form-list__element'
            validate={messages.job_id}
          >
            <div className='form-container'>
              {renderJobsContainer()}
              <div className='form-container__item'>
                <button className='btn--sub' onClick={addJob}>
                  追加する
                </button>
              </div>
              {!newMode && (
                <div className='form-container__item'>
                  <span className='job-type__label'>
                    ※現場で既に予定が登録されている職種は変更できません。
                  </span>
                </div>
              )}
            </div>
          </FormElement>
        </li>
      )}

      <li className='form-list__item'>
        <div className='form-list__label'>
          <span className='label'>ユーザー種別</span>
        </div>
        <FormElement
          className='form-list__element'
          validate={messages.role_code}
        >
          <div className='form-list-radio'>{roleCodeInput()}</div>
        </FormElement>
      </li>

      <li className='form-list__item'>
        <div className='form-list__label'>
          <span className='label'>所属会社</span>
        </div>
        <FormElement className='form-list__element'>
          <input type='text' disabled defaultValue={values.company_name} />
        </FormElement>
      </li>

      {!_.isUndefined(values.company_id) &&
        newMode &&
        isManagerPermission &&
        isTargetRoleId &&
        Number(values.company_id) === Number(companyId) && (
          <li className='form-list__item'>
            <div className='form-list__label'>
              <span className='label'>
                支店
                <SwitchableRequireIcon newMode={newMode} />
              </span>
            </div>
            <FormElement
              className='form-list__element'
              info={
                initPartnerCompanyBranchId === HEAD_OFFICE_OPTION.id
                  ? '支店はデフォルトで本社が選択されています。'
                  : '現場体制に追加された支店がデフォルトで選択されています。'
              }
            >
              <PartnerCompanyBranchDropdown
                selectedCompanyId={values.company_id}
                selectedPartnerCompanyBranchId={
                  _.get(
                    values,
                    'partner_company_branch_id',
                    HEAD_OFFICE_OPTION.id
                  ) || HEAD_OFFICE_OPTION.id
                }
                onSelect={onSelect}
                previewValue={partnerCompanyBranchPreviewName}
                fixedOptions={[HEAD_OFFICE_OPTION]}
                params={{ call_from: CALL_FROM_PAGE.FIELD_TREE_PAGE }}
              />
            </FormElement>
          </li>
        )}

      <li className='form-list__item'>
        <div className='form-list__label'>
          <span className='label'>
            姓<SwitchableRequireIcon newMode={newMode} />
          </span>
        </div>
        <FormElement
          className='form-list__element'
          validate={messages.name_sei}
        >
          <input
            type='text'
            name='name_sei'
            placeholder={
              !disabledFields.includes('name_sei') ? '（例）山田' : null
            }
            onChange={e =>
              forms.set('name_sei', deleteControlCode(e.target.value))
            }
            onBlur={forms.isValid}
            disabled={disabledFields.includes('name_sei')}
            defaultValue={values.name_sei}
          />
        </FormElement>
      </li>

      <li className='form-list__item'>
        <div className='form-list__label'>
          <span className='label'>
            名<SwitchableRequireIcon newMode={newMode} />
          </span>
        </div>
        <FormElement
          className='form-list__element'
          validate={messages.name_mei}
        >
          <input
            type='text'
            name='name_mei'
            placeholder={
              !disabledFields.includes('name_mei') ? '（例）太郎' : null
            }
            onChange={e =>
              forms.set('name_mei', deleteControlCode(e.target.value))
            }
            onBlur={forms.isValid}
            disabled={disabledFields.includes('name_mei')}
            defaultValue={values.name_mei}
          />
        </FormElement>
      </li>

      <li className='form-list__item'>
        <div className='form-list__label'>
          <span className='label'>
            せい
            <SwitchableRequireIcon newMode={newMode} />
          </span>
        </div>
        <FormElement
          className='form-list__element'
          validate={messages.kana_sei}
        >
          <input
            type='text'
            name='kana_sei'
            placeholder={
              !disabledFields.includes('kana_sei') ? '（例）やまだ' : null
            }
            onChange={e =>
              forms.set('kana_sei', deleteControlCode(e.target.value))
            }
            onBlur={forms.isValid}
            disabled={disabledFields.includes('kana_sei')}
            defaultValue={values.kana_sei}
          />
        </FormElement>
      </li>

      <li className='form-list__item'>
        <div className='form-list__label'>
          <span className='label'>
            めい
            <SwitchableRequireIcon newMode={newMode} />
          </span>
        </div>
        <FormElement
          className='form-list__element'
          validate={messages.kana_mei}
        >
          <input
            type='text'
            name='kana_mei'
            placeholder={
              !disabledFields.includes('kana_mei') ? '（例）たろう' : null
            }
            onChange={e =>
              forms.set('kana_mei', deleteControlCode(e.target.value))
            }
            onBlur={forms.isValid}
            disabled={disabledFields.includes('kana_mei')}
            defaultValue={values.kana_mei}
          />
        </FormElement>
      </li>

      <li className='form-list__item'>
        <div className='form-list__label'>
          <span className='label'>電話番号</span>
        </div>
        <FormElement className='form-list__element' validate={messages.tel}>
          {!isTelEmptyAndDisabled() && (
            <label htmlFor='tel' className='input-tel'>
              <input
                type='text'
                name='tel1'
                placeholder={!disabledFields.includes('tel1') ? '03' : null}
                maxLength='5'
                defaultValue={values.tel1}
                onChange={e =>
                  forms.set('tel1', deleteControlCode(e.target.value))
                }
                onBlur={e => forms.isValid(e, 'tel')}
                disabled={disabledFields.includes('tel1')}
              />
              <span>-</span>
              <input
                type='text'
                name='tel2'
                placeholder={!disabledFields.includes('tel2') ? '1234' : null}
                maxLength='4'
                defaultValue={values.tel2}
                onChange={e =>
                  forms.set('tel2', deleteControlCode(e.target.value))
                }
                onBlur={e => forms.isValid(e, 'tel')}
                disabled={disabledFields.includes('tel2')}
              />
              <span>-</span>
              <input
                type='text'
                name='tel3'
                placeholder={!disabledFields.includes('tel3') ? '4567' : null}
                maxLength='4'
                defaultValue={values.tel3}
                onChange={e =>
                  forms.set('tel3', deleteControlCode(e.target.value))
                }
                onBlur={e => forms.isValid(e, 'tel')}
                disabled={disabledFields.includes('tel3')}
              />
            </label>
          )}
        </FormElement>
      </li>

      {(!isForemanRoleId(values.role_code) || !values.integrated) && (
        <li className='form-list__item'>
          <div className='form-list__label'>
            <span className='label'>
              ユーザーID
              <SwitchableRequireIcon newMode={newMode} />
            </span>
          </div>
          <FormElement
            className='form-list__element'
            validate={messages.login_id}
            additionalElement={
              isForemanRoleId(values.role_code) &&
              newMode && (
                <span className='form-list__supplemental-info'>
                  {messageUtility.getMessage('I-001-252')}
                </span>
              )
            }
          >
            <input
              type='text'
              name='login_id'
              onChange={e =>
                forms.set('login_id', deleteControlCode(e.target.value))
              }
              onBlur={forms.isValid}
              disabled={disabledFields.includes('login_id')}
              defaultValue={values.login_id}
            />
          </FormElement>
        </li>
      )}

      <li className='form-list__item'>
        <div className='form-list__label'>
          <span className='label'>
            {isForemanRoleId(values.role_code) && values.integrated
              ? 'ユーザーID（メールアドレス）'
              : 'メールアドレス'}
            {isForemanRoleId(values.role_code) && newMode && (
              <span className='form_label__icon form_label__icon--recommended'>
                推奨
              </span>
            )}
          </span>
        </div>
        <FormElement
          className='form-list__element'
          validate={messages.email}
          additionalElement={
            isForemanRoleId(values.role_code) &&
            newMode && (
              <span className='form-list__supplemental-info'>
                {messageUtility.getMessage('I-001-418')}
              </span>
            )
          }
        >
          <input
            type='text'
            name='email'
            placeholder={
              !disabledFields.includes('email') ? 'メールアドレス' : null
            }
            onChange={e =>
              forms.set('email', deleteControlCode(e.target.value))
            }
            onBlur={forms.isValid}
            disabled={disabledFields.includes('email')}
            defaultValue={values.email}
            className='form-list__input-large'
          />
        </FormElement>
      </li>
      {!newMode && (
        <React.Fragment>
          <li className='form-list__item'>
            <div className='form-list__label'>
              <span className='label'>ステータス</span>
            </div>
            <FormElement className='form-list__element'>
              <input
                type='text'
                disabled={true}
                value={isBlockedUser ? '作業終了' : '稼働中'}
                className='form-list__input-large'
              />
            </FormElement>
          </li>
          <li className='form-list__item'>
            <div className='form-list__label'>
              <span className='label'>招待したユーザー</span>
            </div>
            <FormElement className='form-list__element'>
              <InviterUser
                content={getDisplayInviterUser()}
                isDisplayTooltip={isDisplayInviterUserTooltip()}
              />
            </FormElement>
          </li>
          <li className='form-list__item'>
            <div className='form-list__label'>
              <span className='label'>招待した日時</span>
            </div>
            <FormElement className='form-list__element'>
              <input
                type='text'
                disabled={true}
                value={getCreatedTime()}
                className='form-list__input-large'
              />
            </FormElement>
          </li>
          <li className='form-list__item'>
            <div className='form-list__label'>
              <span className='label'>現場にアクセス可能になった日時</span>
            </div>
            <FormElement
              className='form-list__element'
              additionalElement={
                getDisplayFieldAccessibleTime() ===
                  oldVersionFieldAccessibleTimeString && (
                  <span className='form-list__supplemental-info'>
                    ※現場へのアクセス情報は
                    {ACCESS_CONTROL_INFO_RECORDING_START_DATE}
                    に追加されたため、それ以前の情報は表示されません。
                  </span>
                )
              }
            >
              <input
                type='text'
                disabled={true}
                value={getDisplayFieldAccessibleTime()}
                className='form-list__input-large'
              />
            </FormElement>
          </li>
          <li className='form-list__item'>
            <div className='form-list__label'>
              <span className='label'>作業終了した日時</span>
            </div>
            <FormElement className='form-list__element'>
              <input
                type='text'
                disabled={true}
                value={getBlockedTime()}
                className='form-list__input-large'
              />
            </FormElement>
          </li>
        </React.Fragment>
      )}
    </ul>
  );
};

const InviterUser = ({ content, isDisplayTooltip }) => {
  const contentElement = <p className='inviter_user'>{content}</p>;
  const tooltipStyle = {
    wordWrap: 'break-word',
    whiteSpace: 'pre-wrap',
    color: '#FFF',
    fontSize: '14px',
    lineHeight: '1.5',
    textAlign: 'left',
    maxWidth: '360px',
  };
  return isDisplayTooltip ? (
    <Tippy
      duration={10}
      distance={24}
      arrowType='sharp'
      delay={[500, 0]}
      placement='bottom'
      followCursor={true}
      role={`tippy_target_${_.uniqueId()}`}
      html={<div style={tooltipStyle}>{content}</div>}
    >
      {contentElement}
    </Tippy>
  ) : (
    contentElement
  );
};



// WEBPACK FOOTER //
// ./src/components/forms/TreePartnerUserForm.jsx